import React, { useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useConnectFourMP } from "./hooks/useConnectFourMP";
import ConnectFourBoard from "./ConnectFourBoard";
import ConnectFourStatusBar from "./ConnectFourStatusBar";
import ConnectFourResultModal from "./ConnectFourResultModal";
import useMeasuredAvailableHeight from "../../hooks/useMeasuredAvailableHeight";
import GameBoardFrame from "../games/GameBoardFrame";

export default function ConnectFourMPPage() {
  const { id: gameId } = useParams();
  const statusBarRef = useRef(null);
  const belowBoardRef = useRef(null);
  const boardMaxHeightPx = useMeasuredAvailableHeight(statusBarRef, belowBoardRef);
  const boardMaxWidthStyle =
    boardMaxHeightPx != null
      ? `min(92vw, 640px, ${((boardMaxHeightPx * 7) / 6).toFixed(1)}px)`
      : "min(92vw, 640px)";
  const {
    state,
    dropColumn,
    requestRematch,
    acceptRematch,
    declineRematch,
  } = useConnectFourMP(gameId);
  const {
    board, currentTurn, winner, winCells,
    myPiece, status, isCompleted, wsStatus,
    playerOneName, playerTwoName,
    rematchMessage, rematchShowActions, rematchPending, rematchButtonLocked,
  } = state;

  const handlePlayAgain = useCallback(() => {
    requestRematch();
  }, [requestRematch]);

  const isGameOver = isCompleted || status === "won" || status === "draw";

  return (
    <GameBoardFrame>
      {status === "loading" && (
        <div className="text-text-secondary text-sm py-8">Loading game…</div>
      )}

      {status === "error" && (
        <div className="text-brand-rose text-sm py-4">{state.errorMsg}</div>
      )}

      {status !== "loading" && status !== "error" && (
        <>
          <div className="w-full flex flex-col items-center">
            <div ref={statusBarRef} className="w-full">
              <ConnectFourStatusBar
                status={status}
                currentTurn={currentTurn}
                myPiece={myPiece}
                winner={winner}
                p1Name={playerOneName}
                p2Name={playerTwoName}
                isAI={false}
                wsStatus={wsStatus}
                maxWidthStyle={boardMaxWidthStyle}
              />
            </div>

            {board && (
              <ConnectFourBoard
                board={board}
                winCells={winCells}
                myPiece={myPiece}
                currentTurn={currentTurn}
                isGameOver={isGameOver}
                isDisabled={isGameOver || status === "waiting"}
                lastDrop={null}
                onColumnClick={dropColumn}
                maxWidthStyle={boardMaxWidthStyle}
              />
            )}
          </div>

          <div ref={belowBoardRef} className="w-full min-h-[76px] sm:min-h-[96px] flex items-start justify-center">
            <ConnectFourResultModal
              status={status}
              winner={winner}
              myPiece={myPiece}
              p1Name={playerOneName}
              p2Name={playerTwoName}
              isAI={false}
              onPlayAgain={handlePlayAgain}
              rematchMessage={rematchMessage}
              rematchShowActions={rematchShowActions}
              rematchPending={rematchPending}
              rematchButtonLocked={rematchButtonLocked}
              onAcceptRematch={acceptRematch}
              onDeclineRematch={declineRematch}
            />
          </div>
        </>
      )}
    </GameBoardFrame>
  );
}
